import { useState } from "react";
import { TENANT_DOMAIN } from "../../utils/domain";
import { ERROR_MESSAGES } from "../../constant/constant";
import { useAxios } from "../../utils/useAxios";

/**
 * @typedef {object} OrganizationReference
 * @property {number} id - Institution ID
 * @property {string} name - Institution name
 * @property {"organization" | "sub" | "self"} type - Institution type self meaning independent school and sub means school is under organization
 * @property {string|null} image_url - Institution image URL
 */

/**
 * Custom hook to fetch organization/institution reference details
 */
export const useGetOrganizationReference = () => {
  const [referenceData, setReferenceData] = useState(
    /** @type {OrganizationReference | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch Organization/Institution Reference Details
   * @async
   * @param {object} params
   * @param {number | string} params.institutionId - Institution ID (required)
   * @returns {Promise<void>}
   */
  const getOrganizationReference = async ({ institutionId } = {}) => {
    if (!institutionId) {
      setError("Institution ID is required");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/references/institution/${institutionId}/type`;
      const response = await axiosInstance.get(url);

      setReferenceData(response?.data?.data || null);
    } catch (err) {
      setReferenceData(null);

      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    referenceData,
    loading,
    error,
    getOrganizationReference,
  };
};

